// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useState } from 'react';

import type { HoleShapeFeature } from './useHoleFeatures';
import { parsePolygonRing } from './useCourseAnalysis';

/**
 * What an analysis run found, before promotion decided what the course would say.
 *
 * The published boundaries are one answer per piece of ground: a detection landing inside an
 * OSM ring, on a rejected row, or under a better-ranked provider is dropped on arrival, and a
 * model run is never promoted at all. Judging a detector by them therefore measures promotion,
 * not detection — the shapes it got right on a well-mapped course are exactly the ones thrown
 * away. So this reads the raw candidates, which is the only way the classical detector and the
 * model can be looked at on the same hole and compared.
 */

/** One provider's raw output within one run. */
export type CandidateRun = {
  runId: string;
  /** classical | model. */
  detector: string;
  startedAt: string | null;
  provider: string;
  /** The checkpoint behind a model run; null for the classical detector. */
  providerVersion: string | null;
  /** That checkpoint's two-word name, when it is a registered model. */
  modelNickname: string | null;
  count: number;
};

/**
 * What tells two runs apart for drawing: the detector, and for a model run the checkpoint
 * behind it. Every model the fleet holds is run against a course, so "model" alone is no
 * longer one answer — it is several, and merging them would read as one detector having
 * found everything two or three times over.
 */
export const candidateKey = (run: Pick<CandidateRun, 'detector' | 'providerVersion'>): string =>
  run.providerVersion ? `${run.detector}:${run.providerVersion}` : run.detector;

type Row = Record<string, unknown>;

const str = (row: Row, ...keys: string[]): string => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return '';
};

const num = (row: Row, ...keys: string[]): number => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return 0;
};

/**
 * The source string candidate shapes carry, so the modal's existing filtering, listing and
 * drawing work on them unchanged. Deliberately not `satellite`: a candidate is not a published
 * row and must never be mistaken for one — it has no review status, no ordinal and no id a
 * person can accept.
 */
export const CANDIDATE_SOURCE = 'candidate';

export type AnalysisCandidatesState = {
  /** Candidate rings, shaped like published boundaries so the same code draws them. */
  shapes: HoleShapeFeature[];
  /** One entry per provider result, newest run first — what the picker labels itself with. */
  runs: CandidateRun[];
  loading: boolean;
};

/**
 * A course's recent analysis candidates, grouped into drawable shapes.
 *
 * `sourceProvider` carries the **detector**, not the imagery provider: what a reader wants to
 * switch on and off here is "show me what the model found", and the imagery is already named
 * by the basemap picker.
 */
export function useAnalysisCandidates(
  apiBaseUrl: string,
  token: string | null | undefined,
  courseId: string | null,
  /** Fetched only when the admin has something to compare — see the modal's own gate. */
  enabled: boolean,
  /** Bumped by the caller to re-read after a run finishes. */
  reloadKey = 0
): AnalysisCandidatesState {
  const [shapes, setShapes] = useState<HoleShapeFeature[]>([]);
  const [runs, setRuns] = useState<CandidateRun[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !token || !courseId) {
      setShapes([]);
      setRuns([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    const load = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/geodata/courses/${encodeURIComponent(courseId)}/analysis-candidates`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;

        if (!response.ok) {
          // Nothing to say: an older backend without the route, or a caller without the
          // admin role, both mean "no candidates to compare" as far as this screen goes.
          setShapes([]);
          setRuns([]);
          return;
        }

        const body = (await response.json()) as Row[] | null;
        if (cancelled || !Array.isArray(body)) {
          setShapes([]);
          setRuns([]);
          return;
        }

        const nextRuns: CandidateRun[] = [];
        // Kept per run so the stale-run filter below can drop a run's shapes with it.
        const shapesByRun = new Map<string, HoleShapeFeature[]>();

        for (const row of body) {
          const detector = str(row, 'detector', 'Detector') || 'classical';
          const rawCandidates = row.candidates ?? row.Candidates;
          const candidates = Array.isArray(rawCandidates) ? (rawCandidates as Row[]) : [];

          const runId = str(row, 'runId', 'RunId');
          nextRuns.push({
            runId,
            detector,
            startedAt: str(row, 'startedAt', 'StartedAt') || null,
            provider: str(row, 'provider', 'Provider'),
            providerVersion: str(row, 'providerVersion', 'ProviderVersion') || null,
            modelNickname: str(row, 'modelNickname', 'ModelNickname') || null,
            count: candidates.length,
          });

          const runShapes = shapesByRun.get(runId) ?? [];
          shapesByRun.set(runId, runShapes);

          for (const candidate of candidates) {
            const id = str(candidate, 'id', 'Id');
            const kind = str(candidate, 'kind', 'Kind');
            const ring = parsePolygonRing(
              str(candidate, 'polygonGeoJson', 'PolygonGeoJson') || null
            );
            if (!id || !kind || !ring) continue;

            runShapes.push({
              id,
              kind,
              ring,
              holeNumber: num(candidate, 'holeNumber', 'HoleNumber'),
              name: null,
              source: CANDIDATE_SOURCE,
              // The run's key, not just its detector: one course now carries a model run per
              // installed model, and the map has to be able to show one without the others.
              sourceProvider: candidateKey({
                detector,
                providerVersion: str(row, 'providerVersion', 'ProviderVersion') || null,
              }),
              confidence: num(candidate, 'confidence', 'Confidence'),
              sampleCount: 0,
            });
          }
        }

        // Only the newest run per detector *and model* is drawable: two runs of the same model
        // over the same course are the same shapes twice, one of them stale, and drawn together
        // they read as the model having found everything twice. Keyed by model as well because
        // every installed model is run against a course — keyed by detector alone, the newest
        // model would be the only one anybody could ever see, and the rest would be collected
        // and silently dropped here.
        const newestByModel = new Map<string, string>();
        for (const run of nextRuns) {
          const key = candidateKey(run);
          if (!newestByModel.has(key)) newestByModel.set(key, run.runId);
        }
        const kept = new Set(
          nextRuns
            .filter((run) => newestByModel.get(candidateKey(run)) === run.runId)
            .map((run) => run.runId)
        );

        setRuns(nextRuns.filter((run) => kept.has(run.runId)));
        setShapes([...kept].flatMap((runId) => shapesByRun.get(runId) ?? []));
      } catch {
        if (!cancelled) {
          setShapes([]);
          setRuns([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, courseId, enabled, token, reloadKey]);

  return { shapes, runs, loading };
}
